import { useUserControlsContext } from '../../../web/app-home/src/contexts/UserControlsContext';

/**
 * Stop or switch impersonation, then the caller reloads. The same core hooks
 * the banner and both global palettes use:
 *
 *   `useSwitchImpersonation()(user)`   switch to someone you have an
 *                                      `ownerEmail` + `slackUserId` for; also
 *                                      writes the shared recents entry.
 *   `useSubmitImpersonationMapping()`  takes the server's command directly —
 *                                      `STOP_IMPERSONATING` (which also drops
 *                                      the write unlock), or
 *                                      `{ action: 'start', target }` when you
 *                                      hold only one identifier.
 *   `describeImpersonationError(err)`  the refusal, in words.
 *
 * Never hand-build the request body: the command is one zod schema
 * (`src/shared/schemas/impersonation.ts`), and a local copy of it going stale
 * is what broke Delphi's ⌘I in production on 2026-09-17.
 */
export {
  useSubmitImpersonationMapping,
  useSwitchImpersonation,
  describeImpersonationError,
  STOP_IMPERSONATING,
} from '../../../web/app-home/src/hooks/useImpersonation';
export type {
  ImpersonationCommand,
  ImpersonationTarget,
} from '../../../web/app-home/src/hooks/useImpersonation';

/**
 * Core's validated `/api/admin/me` reader, its person shape, and its typed
 * failure — so a mini-app picker reads the people list and reports a refusal
 * the same way core does, instead of hand-rolling the request and declaring a
 * structurally-compatible twin of the row. A local copy is what let the ⌘I
 * palette drift off the wire contract in the first place (2026-09-17).
 */
export { fetchAdminMe, AdminApiError } from '../../../web/app-home/src/api/admin';
export type { OwnerOption } from '../../../src/shared/schemas/admin-access';

export interface ImpersonationState {
  /** True while the signed-in user is acting as someone else. */
  active: boolean;
  /** Whether this viewer may unlock impersonated writes (`impersonation_writer`; admin satisfies it). */
  canUnlockWrites: boolean;
  /**
   * The acting user's owner email (`actingUser.ownerEmail`), null when not
   * impersonating. It is all `/api/me` knows — Terra's `User` has no name —
   * but it arrives before any app data, so a surface can label its own
   * stop control while its board is still loading or has failed.
   *
   * It can also be null WHILE impersonating: a Terra user without an
   * `ownerEmail` is a valid delegation (`src/__tests__/api/me-impersonation.test.ts`),
   * and one whose board request will 401 — so a surface must not gate its
   * stop control on this being present. Fall back to `actingAsId`.
   */
  actingAs: string | null;
  /** The acting user's Slack id — the identifier that survives a missing owner email. */
  actingAsId: string | null;
}

/**
 * Who the app is acting as, per `/api/me` — state only.
 *
 * Deliberately NOT a re-export of core's `useImpersonation()`: that hook also
 * owns the non-production `?impersonate=` URL trigger, and the shell's banner
 * already runs it once per page (it stays mounted on every authenticated
 * route, Delphi included — it only stops *rendering* there). A second instance
 * would POST the mapping twice on the same URL. Mini-apps read state here and
 * act through `useSubmitImpersonationMapping`.
 */
export function useImpersonationState(): ImpersonationState {
  const { impersonation } = useUserControlsContext();
  return {
    active: impersonation?.active === true,
    canUnlockWrites: impersonation?.canUnlockWrites === true,
    actingAs: impersonation?.actingAs ?? null,
    actingAsId: impersonation?.actingAsSlackId ?? null,
  };
}
